/*
 * @Author: lizhigang
 * @Date: 2025-10-20 17:37:15
 * @Company: www.smm.cn
 * @Description: 登录 Modal 组件 - 使用 Context + Hook 模式，支持多语言
 */
"use client"

import "./common.scss"

import type { HookAPI } from "antd/es/modal/useModal"
import { createContext, ReactNode, useContext, useRef, useState } from "react"
import { Drawer, Modal, ModalFuncProps } from "antd"

import ForgotPassword from "./_components/forgot"
import Login from "./_components/login"
import ModalContent from "./_components/modal"
import Register from "./_components/register"

export type SignInType = "login" | "register" | "forgot"

export interface ActionCallbacks {
  onLoginSuccess?: (msg?: string) => void
  onRegisterSuccess?: (msg?: string) => void
  onForgotSuccess?: (msg?: string) => void
}

// 扩展 ModalFuncProps，添加 language 参数
interface SignInModalConfig extends Omit<ModalFuncProps, "content" | "okText" | "cancelText">, ActionCallbacks {
  animate?: "modal" | "drawer"
  language?: string
  content?: ReactNode
  action?: SignInType
}

// 定义 Context 类型
interface SignInModalContextType {
  showSignInModal: (config?: SignInModalConfig) => void
  modal: HookAPI // Ant Design Modal 实例
}

// 创建 Context
const SignInModalContext = createContext<SignInModalContextType | undefined>(undefined)

// Provider 组件
export const SigninModalProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false)
  const [language, setLanguage] = useState("en")
  const [action, setAction] = useState<SignInType>("login")
  const [actionCallbacks, setActionCallbacks] = useState<ActionCallbacks | null>(null)
  const [modal, contextHolder] = Modal.useModal()
  const modalRef = useRef<any>(null)

  // 获取默认配置（根据语言）
  const getDefaultConfig = ({
    action = "login",
    language,
    content,
    onLoginSuccess,
    onRegisterSuccess,
    onForgotSuccess,
  }: {
    action?: SignInType
    language?: string
    content?: ReactNode
  } & ActionCallbacks): ModalFuncProps => {
    return {
      icon: null,
      centered: true,
      maskClosable: true,
      className: "signIn-modal",
      destroyOnHidden: true, // antd 类型定义尚未更新，但运行时支持
      content: content || (
        <ModalContent
          language={language}
          action={action}
          onClose={() => modalRef.current?.destroy()}
          onLoginSuccess={onLoginSuccess}
          onRegisterSuccess={onRegisterSuccess}
          onForgotSuccess={onForgotSuccess}
        />
      ),
      footer: null,
    } as ModalFuncProps
  }

  // 显示登录 Modal 的方法
  const showSignInModal = (config?: SignInModalConfig) => {
    const {
      language,
      action,
      content,
      animate = "modal",
      onLoginSuccess,
      onRegisterSuccess,
      onForgotSuccess,
      ...restConfig
    } = config || {}

    // 获取对应语言的默认配置
    const defaultConfig = getDefaultConfig({
      action,
      language,
      content,
      onLoginSuccess,
      onRegisterSuccess,
      onForgotSuccess,
    })

    if (animate === "modal") {
      modalRef.current = modal.info({
        ...defaultConfig,
        wrapClassName: "signin-modal", // 默认应用 signin-modal 样式类
        ...restConfig, // 允许覆盖默认配置（包括 wrapClassName）
      })
    }
    if (animate === "drawer") {
      setOpen(true)
      if (language) {
        setLanguage(language)
      }
      if (action) {
        setAction(action)
      }
      const callbacks: ActionCallbacks = {}
      if (onLoginSuccess) {
        callbacks.onLoginSuccess = onLoginSuccess
      }
      if (onRegisterSuccess) {
        callbacks.onRegisterSuccess = onRegisterSuccess
      }
      if (onForgotSuccess) {
        callbacks.onForgotSuccess = onForgotSuccess
      }
      if (callbacks) {
        setActionCallbacks(callbacks)
      }
    }
  }

  const onDrawerClose = () => {
    setOpen(false)
    setLanguage("en")
    setAction("login")
    setActionCallbacks(null)
  }

  return (
    <SignInModalContext.Provider value={{ showSignInModal, modal }}>
      {children}
      {contextHolder}
      <Drawer
        className="signin-drawer"
        open={open}
        placement="bottom"
        height="100%"
        closable={false}
        zIndex={1002}
        destroyOnHidden
      >
        <ModalContent
          language={language}
          action={action}
          onClose={onDrawerClose}
          onLoginSuccess={actionCallbacks?.onLoginSuccess}
          onRegisterSuccess={actionCallbacks?.onRegisterSuccess}
          onForgotSuccess={actionCallbacks?.onForgotSuccess}
        />
      </Drawer>
    </SignInModalContext.Provider>
  )
}

// 自定义 Hook
export const useSigninModal = () => {
  const context = useContext(SignInModalContext)
  if (!context) {
    throw new Error("useSigninModal must be used within SigninModalProvider")
  }
  return context
}

export { ForgotPassword, Login, Register }

// 兼容旧代码：默认导出 Provider
export default SigninModalProvider
